/**
 * App.jsx — Main application shell
 *
 * Layout:
 *  ┌─────────────────────────────────────────────────────┐
 *  │  Header (green bar) — title + filter dropdowns      │
 *  ├───────────┬─────────────────────────────────────────┤
 *  │  Sidebar  │   Leaflet Map (full height)             │
 *  │  Stats    │                                         │
 *  │  Upload   │                                         │
 *  └───────────┴─────────────────────────────────────────┘
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import MapDashboard from './components/MapDashboard';
import Sidebar from './components/Sidebar';
import StatsPanel from './components/StatsPanel';
import ImageUpload from './components/ImageUpload';
import Legend from './components/Legend';
import DroneStatus from './components/DroneStatus';
import DroneControls from './components/DroneControls';
import GPSLocator from './components/GPSLocator';

const API_BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const GRID_STEP = 0.0013;  // ~145 m per tile

// Coordinates for each selectable location (district / taluk / village)
const LOCATION_COORDS = {
  'Edakelayur':        [10.748, 79.413],
  'Valangaiman':       [10.888, 79.569],
  'Thiruthuraipoondi': [10.523, 79.650],
  'Vedaranyam':        [10.373, 79.850],
  'Koothanallur':      [10.820, 79.530],
  'Needamangalam':     [10.800, 79.450],
  'Mannargudi':        [10.666, 79.450],
  'Thanjavur':         [10.787, 79.139],
  'Papanasam':         [10.945, 79.272],
  'Kumbakonam':        [10.961, 79.383],
  'Thiruvarur':        [10.773, 79.637],
};

// ─── Simulated drone flight (lawnmower pattern) ───────────────────────────────
const SIM_CENTER = [10.748, 79.413];

function genLawnmowerPath(centerLat, centerLon) {
  const points = [];
  const rows = 6, cols = 9;
  const latStart = centerLat + 0.006;
  const lonStart = centerLon - 0.010;
  const latStep  = -0.002, lonStep = 0.0022;
  for (let r = 0; r < rows; r++) {
    const lat  = latStart + r * latStep;
    const even = r % 2 === 0;
    for (let c = 0; c < cols; c++) {
      const col = even ? c : cols - 1 - c;
      points.push({ lat: +(lat).toFixed(6), lon: +(lonStart + col * lonStep).toFixed(6) });
    }
  }
  return points;
}

function useSimulatedDrone(isCapturing) {
  const [simPos,  setSimPos]  = useState(null);
  const [simPath, setSimPath] = useState([]);
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isCapturing) {
      const route = genLawnmowerPath(SIM_CENTER[0], SIM_CENTER[1]);
      indexRef.current = 0;
      setSimPath([]);
      setSimPos(null);

      const tick = () => {
        if (indexRef.current >= route.length) indexRef.current = 0;
        const pt = route[indexRef.current++];
        setSimPos({ lat: pt.lat, lon: pt.lon, alt: 45, sats: 10, valid: true });
        setSimPath(prev => [...prev.slice(-200), [pt.lat, pt.lon]]);
      };

      tick();
      timerRef.current = setInterval(tick, 900);
    } else {
      clearInterval(timerRef.current);
      setSimPos(null);
      setSimPath([]);
      indexRef.current = 0;
    }
    return () => clearInterval(timerRef.current);
  }, [isCapturing]);

  return { simPos, simPath };
}

// ─── Initial state ────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mob, setMob] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const fn = e => setMob(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return mob;
}

const DEFAULT_FILTERS = {
  district: 'Thiruvarur',
  taluk: 'Needamangalam',
  village: 'Edakelayur',
  surveyNumber: '',
};

export default function App() {
  const [filters, setFilters]         = useState(DEFAULT_FILTERS);
  const [viewMode, setViewMode]       = useState('crop');   // 'crop' | 'stage'
  const [selectedParcel, setSelected] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [sidebarTab, setSidebarTab]   = useState('stats');
  const [mapRef, setMapRef]           = useState(null);
  const [gpsMarker, setGpsMarker]     = useState(null);   // {lat, lon, accuracy, result}
  const [gpsTrail,  setGpsTrail]      = useState([]);     // [{lat,lon}] walked path
  const [gpsHistory,setGpsHistory]    = useState([]);     // [{lat,lon,result}] scan zones
  const [isCapturing, setIsCapturing] = useState(false);
  const { simPos, simPath } = useSimulatedDrone(isCapturing);
  const [mobileSheet, setMobileSheet] = useState(false);
  const isMobile = useIsMobile();

  const toggleCapture = useCallback(() => {
    const next = !isCapturing;
    setIsCapturing(next);
    fetch(`${API_BASE}/api/drone/command`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command: next ? 'START_CAPTURE' : 'STOP_CAPTURE' }),
    }).catch(() => {});
  }, [isCapturing]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleParcelClick = useCallback((parcel) => {
    setSelected(parcel);
  }, []);

  const handleFlyTo = useCallback((lat, lon, zoom = 15) => {
    if (mapRef) mapRef.flyTo([lat, lon], zoom, { animate: true, duration: 1.2 });
  }, [mapRef]);

  /**
   * Paint a ~4 km × 4 km Voronoi land-classification map centred on the GPS fix.
   *
   * Instead of per-tile random colours (checkerboard), we scatter ~55 "field seeds"
   * across the area. Every tile takes the crop of its nearest seed → contiguous
   * field patches that look like real agricultural land, not a noisy grid.
   */
  /**
   * Central classifier — works for ANY location:
   *   • GPS first fix
   *   • Village / district dropdown change
   * Generates Voronoi field patches around centerLat/centerLon.
   */
  const classifyArea = useCallback((centerLat, centerLon) => {
    // At zoom 16 the viewport is ~0.010° lat × 0.015° lon.
    // Use a fixed ±0.014° extent so ~40×40 fine tiles fill the screen edge-to-edge.
    const latHalf = 0.014;
    const lonHalf = 0.016;
    const north = centerLat + latHalf;
    const south = centerLat - latHalf;
    const east  = centerLon + lonHalf;
    const west  = centerLon - lonHalf;

    // Fine step ~70 m → gives 40 × 46 ≈ 1840 tiles (detailed accuracy)
    const step = 0.00065;

    // Cauvery delta weighted crop palette
    const palette = [
      'Paddy','Paddy','Paddy','Paddy',   // 44 % Paddy
      'Sugarcane','Sugarcane','Sugarcane',// 33 % Sugarcane
      'Coconut','Coconut',               // 22 % Coconut
      'Banana','Groundnut','Fallow',     //  rest
    ];
    const stages = ['Early','Intermediate','Mature','Harvested'];

    // Deterministic LCG helper — seeded from GPS coords so same location = same map
    function rand(n) {
      // Wang hash of n
      n = (n ^ 61) ^ (n >>> 16);
      n += (n << 3);
      n ^= (n >>> 4);
      n *= 0x27d4eb2d;
      n ^= (n >>> 15);
      return (n >>> 0) / 0xffffffff;   // 0..1
    }
    const seedBase = Math.round(centerLat * 1e4) * 1e4 + Math.round(centerLon * 1e4);

    // Build 55 Voronoi seeds — each becomes one "field"
    const NUM_SEEDS = 90;  // more seeds = smaller, more realistic field patches
    const seeds = Array.from({ length: NUM_SEEDS }, (_, i) => {
      const rx = rand(seedBase + i * 3571);
      const ry = rand(seedBase + i * 7919 + 1234);
      const ri = Math.floor(rand(seedBase + i * 1013) * palette.length);
      const rs = Math.floor(rand(seedBase + i * 2237) * stages.length);
      const rc = rand(seedBase + i * 4421);
      const rn = rand(seedBase + i * 6113);
      return {
        lat:   south + (north - south) * rx,
        lon:   west  + (east  - west)  * ry,
        crop:  palette[ri],
        stage: stages[rs],
        conf:  +(0.68 + rc * 0.22).toFixed(3),
        ndvi:  +(0.18 + rn * 0.62).toFixed(3),
      };
    });

    // Assign each tile to nearest seed (Voronoi)
    const zones = [];
    for (let lat = south + step / 2; lat < north; lat += step) {
      for (let lon = west + step / 2; lon < east; lon += step) {
        let best = seeds[0];
        let bestD = Infinity;
        for (const s of seeds) {
          const d = (lat - s.lat) ** 2 + (lon - s.lon) ** 2;
          if (d < bestD) { bestD = d; best = s; }
        }
        zones.push({
          lat:  +lat.toFixed(6),
          lon:  +lon.toFixed(6),
          step: step,
          result: {
            crop_type:        best.crop,
            growth_stage:     best.stage,
            confidence:       best.conf,
            stage_confidence: +(best.conf - 0.06).toFixed(3),
            ndvi:             best.ndvi,
            tile_source:      'synthetic',
            demo_mode:        true,
          },
        });
      }
    }
    setGpsHistory(zones);

    // Zoom to 16 — shows satellite detail while keeping tiles clearly visible
    if (mapRef) mapRef.flyTo([centerLat, centerLon], 16, { animate: true, duration: 1.0 });
  }, [mapRef]);


  const sidebarContent = (
    <>
      <div className="flex border-b border-slate-700/50">
        {[
          { key: 'stats', label: '📊 Stats' },
          { key: 'gps',   label: '📍 GPS'   },
          { key: 'upload',label: '📷 Upload' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSidebarTab(tab.key)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
              sidebarTab === tab.key
                ? 'text-green-400 border-b-2 border-green-400 bg-green-900/20'
                : 'text-slate-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {sidebarTab === 'stats' && (
          <>
            <Sidebar filters={filters} selectedParcel={selectedParcel} onFlyTo={handleFlyTo} />
            <StatsPanel />
          </>
        )}
        {sidebarTab === 'gps' && (
          <GPSLocator
            onLocationDetected={(loc) => {
              setGpsMarker(prev => {
                if (!prev) setTimeout(() => classifyArea(loc.lat, loc.lon), 1200);
                return { lat: loc.lat, lon: loc.lon, accuracy: loc.accuracy, result: prev?.result ?? null };
              });
            }}
            onResult={(res) => {
              setUploadResult(res);
              setGpsMarker(prev => ({ ...(prev || { lat: res.lat, lon: res.lon }), result: res }));
              setGpsHistory(prev => {
                const filtered = prev.filter(
                  h => Math.abs(h.lat - res.lat) > GRID_STEP / 2 || Math.abs(h.lon - res.lon) > GRID_STEP / 2
                );
                return [...filtered, { lat: res.lat, lon: res.lon, result: res }];
              });
            }}
            onTrailUpdate={(trail) => setGpsTrail(trail)}
            onTrackingStart={() => { setGpsTrail([]); setGpsHistory([]); setGpsMarker(null); }}
          />
        )}
        {sidebarTab === 'upload' && <ImageUpload onResult={setUploadResult} />}
        {uploadResult && sidebarTab === 'upload' && <ResultCard result={uploadResult} />}
      </div>
      <div className="border-t border-slate-700/50 p-2">
        <Legend viewMode={viewMode} />
      </div>
    </>
  );

  const headerStyle = {
    background: 'linear-gradient(135deg, #14532d 0%, #166534 50%, #15803d 100%)',
    borderBottom: '2px solid #22c55e',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-950">

      {/* ══════════════ DESKTOP HEADER (md+) ══════════════════════════════ */}
      <header className="hidden md:flex items-center gap-4 px-4 py-2 z-50 flex-shrink-0" style={headerStyle}>
        <div className="flex items-center gap-3 mr-4">
          <div className="text-2xl select-none">🛰️</div>
          <div>
            <h1 className="text-white font-bold text-base leading-tight tracking-wide">Crop Land Classifier</h1>
            <p className="text-green-200 text-xs">Thanjavur / Thiruvarur · Sentinel-2 AI</p>
          </div>
        </div>
        <div className="w-px h-8 bg-green-600" />
        <div className="flex items-center gap-2 flex-wrap">
          <HeaderSelect label="District" value={filters.district} options={['Thiruvarur','Thanjavur']}
            onChange={v => { handleFilterChange('district',v); const [la,lo]=LOCATION_COORDS[v]||[10.748,79.413]; handleFlyTo(la,lo,13); setGpsHistory([]); setGpsTrail([]); setGpsMarker(null); classifyArea(la,lo); }} />
          <HeaderSelect label="Taluk" value={filters.taluk} options={['Needamangalam','Mannargudi','Thanjavur','Papanasam','Kumbakonam']}
            onChange={v => { handleFilterChange('taluk',v); const [la,lo]=LOCATION_COORDS[v]||[10.748,79.413]; handleFlyTo(la,lo,13); setGpsHistory([]); setGpsTrail([]); setGpsMarker(null); classifyArea(la,lo); }} />
          <HeaderSelect label="Village" value={filters.village} options={['Edakelayur','Valangaiman','Thiruthuraipoondi','Vedaranyam']}
            onChange={v => { handleFilterChange('village',v); const [la,lo]=LOCATION_COORDS[v]||[10.748,79.413]; handleFlyTo(la,lo,14); setGpsHistory([]); setGpsTrail([]); setGpsMarker(null); classifyArea(la,lo); }} />
          <div className="flex items-center bg-green-900/60 border border-green-700 rounded px-2 py-1 gap-1">
            <span className="text-green-300 text-xs font-medium">Survey#</span>
            <input type="text" placeholder="e.g. 417" value={filters.surveyNumber}
              onChange={e => handleFilterChange('surveyNumber', e.target.value)}
              className="bg-transparent text-white text-xs w-16 outline-none placeholder-green-600" />
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1 bg-green-900/60 border border-green-700 rounded-lg p-0.5">
          {['crop','stage'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`px-3 py-1 rounded text-xs font-semibold transition-all ${viewMode===mode ? 'bg-green-500 text-white shadow' : 'text-green-300 hover:text-white'}`}>
              {mode==='crop' ? '🌾 Crop Type' : '📈 Growth Stage'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-green-300">
          <div className="w-2 h-2 rounded-full bg-green-400 pulse-dot" />
          <span>LIVE</span>
        </div>
      </header>

      {/* ══════════════ MOBILE HEADER ═════════════════════════════════════ */}
      <header className="flex md:hidden items-center gap-2 px-3 py-2 z-50 flex-shrink-0" style={headerStyle}>
        <span className="text-xl select-none">🛰️</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm leading-tight">Crop Land Classifier</div>
          <div className="text-green-200 text-xs truncate">{filters.village} · Sentinel-2 AI</div>
        </div>
        <select
          value={filters.village}
          onChange={e => {
            const v = e.target.value;
            handleFilterChange('village', v);
            const [la, lo] = LOCATION_COORDS[v] || [10.748, 79.413];
            handleFlyTo(la, lo, 14);
            setGpsHistory([]); setGpsTrail([]); setGpsMarker(null);
            classifyArea(la, lo);
          }}
          className="bg-green-900/70 border border-green-700 rounded px-2 py-1 text-white text-xs outline-none max-w-28"
        >
          {['Edakelayur','Valangaiman','Thiruthuraipoondi','Vedaranyam'].map(v => (
            <option key={v} value={v} className="bg-slate-900">{v}</option>
          ))}
        </select>
        <button
          onClick={() => setViewMode(m => m === 'crop' ? 'stage' : 'crop')}
          className="p-1.5 rounded-lg bg-green-900/60 border border-green-700 text-base leading-none"
          title={viewMode === 'crop' ? 'Growth Stage' : 'Crop Type'}
        >
          {viewMode === 'crop' ? '🌾' : '📈'}
        </button>
        <div className="w-2 h-2 rounded-full bg-green-400 pulse-dot flex-shrink-0" />
      </header>

      {/* ══════════════ BODY ══════════════════════════════════════════════ */}
      <div className="flex flex-1 overflow-hidden">

        {/* DESKTOP SIDEBAR — only mounted on desktop to avoid double GPSLocator */}
        {!isMobile && (
          <aside className="flex flex-col w-72 flex-shrink-0 overflow-hidden z-40"
            style={{ background: 'rgba(10,17,28,0.95)', borderRight: '1px solid rgba(45,74,110,0.5)' }}>
            {sidebarContent}
          </aside>
        )}

        {/* MAP */}
        <main className="flex-1 relative flex flex-col overflow-hidden">
          <div className="flex-1 relative">
            <MapDashboard
              filters={filters} viewMode={viewMode}
              onParcelClick={handleParcelClick} onMapReady={setMapRef}
              gpsMarker={gpsMarker} gpsTrail={gpsTrail} gpsHistory={gpsHistory}
              simPos={simPos} simPath={simPath}
            />
            <DroneStatus simGps={simPos} isCapturing={isCapturing} onPositionUpdate={() => {}} />
          </div>
          {/* Desktop drone controls */}
          <div className="hidden md:block">
            <DroneControls onCommandSent={(cmd) => {
              if (cmd === 'START_CAPTURE') setIsCapturing(true);
              if (cmd === 'STOP_CAPTURE')  setIsCapturing(false);
            }} />
          </div>
          {/* Mobile spacer — pushes map up so fixed bottom bar doesn't overlap */}
          <div className="block md:hidden flex-shrink-0" style={{ height: 64 }} />
        </main>
      </div>

      {/* ══════════════ MOBILE BOTTOM SHEET — only mounted on mobile ════ */}
      {isMobile && (
        <div
          className={`fixed inset-x-0 z-50 flex flex-col overflow-hidden transition-transform duration-300 ease-out ${mobileSheet ? 'translate-y-0' : 'translate-y-full'}`}
          style={{ bottom: 64, maxHeight: '72vh', background: 'rgba(8,15,26,0.98)', borderTop: '1px solid rgba(45,74,110,0.6)', borderRadius: '18px 18px 0 0' }}
        >
          {/* Drag handle + close */}
          <div className="flex items-center px-4 pt-2.5 pb-1 flex-shrink-0">
            <div className="flex-1 flex justify-center">
              <div className="w-10 h-1 rounded-full bg-slate-600 cursor-pointer" onClick={() => setMobileSheet(false)} />
            </div>
            <button onClick={() => setMobileSheet(false)} className="text-slate-500 text-2xl leading-none ml-2">×</button>
          </div>
          {sidebarContent}
        </div>
      )}

      {/* ══════════════ MOBILE BACKDROP ═══════════════════════════════════ */}
      {isMobile && mobileSheet && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMobileSheet(false)} />
      )}

      {/* ══════════════ MOBILE BOTTOM BAR ════════════════════════════════ */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-50 flex items-center gap-1 px-3"
        style={{ height: 64, background: 'rgba(6,12,22,0.97)', borderTop: '1px solid rgba(45,74,110,0.5)' }}
      >
        {/* Tab buttons */}
        {[
          { key: 'stats',  icon: '📊', label: 'Stats'  },
          { key: 'gps',    icon: '📍', label: 'GPS'    },
          { key: 'upload', icon: '📷', label: 'Upload' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setSidebarTab(tab.key); setMobileSheet(s => sidebarTab === tab.key ? !s : true); }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors ${
              mobileSheet && sidebarTab === tab.key
                ? 'bg-green-900/50 text-green-400'
                : 'text-slate-500 active:bg-slate-800'
            }`}
          >
            <span className="text-lg leading-none">{tab.icon}</span>
            <span className="text-xs font-medium">{tab.label}</span>
          </button>
        ))}

        <div className="flex-1" />

        {/* Drone live chip */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full mr-2 transition-colors ${
          isCapturing ? 'bg-green-900/50' : 'bg-slate-800/60'}`}>
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCapturing ? 'bg-green-400 pulse-dot' : 'bg-slate-500'}`} />
          <span className="text-sm">🚁</span>
          <span className={`text-xs font-bold ${isCapturing ? 'text-green-400' : 'text-slate-500'}`}>
            {isCapturing ? 'LIVE' : 'OFF'}
          </span>
        </div>

        {/* Start / Stop button */}
        <button
          onClick={toggleCapture}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-bold text-sm transition-all active:scale-95 shadow-lg ${
            isCapturing
              ? 'bg-red-600 text-white shadow-red-900/50'
              : 'bg-green-600 text-white shadow-green-900/50'
          }`}
        >
          <span>{isCapturing ? '⏹' : '▶'}</span>
          <span>{isCapturing ? 'Stop' : 'Start'}</span>
        </button>
      </div>

    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function HeaderSelect({ label, value, options, onChange }) {
  return (
    <div className="flex items-center bg-green-900/60 border border-green-700 rounded px-2 py-1 gap-1">
      <span className="text-green-300 text-xs font-medium">{label}:</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-transparent text-white text-xs outline-none cursor-pointer"
      >
        {options.map(opt => (
          <option key={opt} value={opt} className="bg-green-900 text-white">
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function ResultCard({ result }) {
  const CROP_COLORS = {
    Paddy: '#00FF00', Sugarcane: '#FFD700', Banana: '#FF6B35',
    Coconut: '#228B22', Groundnut: '#DEB887', Fallow: '#8B4513',
  };
  const color = CROP_COLORS[result.crop_type] || '#94a3b8';

  return (
    <div
      className="rounded-lg p-3 border fade-in"
      style={{ borderColor: color, background: `${color}15` }}
    >
      <div className="text-xs font-bold text-slate-300 mb-2">
        🔬 Classification Result
      </div>
      <div className="space-y-1.5">
        <ResultRow icon="🌾" label="Crop"       value={result.crop_type}    color={color} bold />
        <ResultRow icon="📈" label="Stage"      value={result.growth_stage} />
        <ResultRow icon="✅" label="Confidence" value={`${(result.confidence * 100).toFixed(1)}%`} />
        <ResultRow icon="🌿" label="NDVI"       value={result.ndvi_value?.toFixed(3) ?? 'N/A'} />
      </div>
    </div>
  );
}

function ResultRow({ icon, label, value, color, bold }) {
  return (
    <div className="flex justify-between items-center text-xs">
      <span className="text-slate-400">{icon} {label}</span>
      <span
        className={`font-${bold ? 'bold' : 'medium'}`}
        style={{ color: color || '#e2e8f0' }}
      >
        {value}
      </span>
    </div>
  );
}
